import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldAlert, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Force-activate dialog. Opens when PATCH /api/tasks/{id} returns 409 on
 * status change of a still-sleeping task. Shows pending upstreams, explains
 * that all conditions are being removed, requires explicit confirmation.
 *
 * Backend records an audit line in `unlock_note`: "Force-activated ...".
 * Button label per product rule: "Aktiviraj ipak" (not "Forsiraj").
 */
export default function ForceActivateDialog({
  open,
  onOpenChange,
  taskId,
  taskTitle,
  blockingTitles = [],       // list of upstream task titles not yet done
  attemptedStatus,           // status the user tried to set (in_progress, done, ...)
  onActivated,
}) {
  const [saving, setSaving] = useState(false);

  const confirm = async () => {
    setSaving(true);
    try {
      const body = { force_activate: true };
      if (attemptedStatus) body.status = attemptedStatus;
      await api.patch(`/tasks/${taskId}`, body);
      toast.success("Zadatak je aktiviran.");
      onOpenChange(false);
      onActivated?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const n = blockingTitles.length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="force-activate-dialog"
        className="bg-white max-w-md max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-ochre" />
            Ovaj zadatak još nije spreman za rad
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-ink">
            „{taskTitle}" čeka {n === 1 ? "jedan prethodni korak" :
                                n >= 2 && n <= 4 ? `${n} prethodna koraka` :
                                `${n} prethodnih koraka`} koji {n === 1 ? "nije završen" : "nisu završeni"}:
          </p>

          <ul className="bg-surface-secondary rounded-md p-3 border border-line space-y-1">
            {blockingTitles.map((title, i) => (
              <li
                key={i}
                data-testid={`force-blocking-${i}`}
                className="text-sm text-ink flex items-start gap-2"
              >
                <span className="text-ochre mt-1">○</span>
                <span className="flex-1 min-w-0 break-words">{title}</span>
              </li>
            ))}
          </ul>

          <p className="text-sm text-ink-muted">
            Ako aktiviraš ipak, sistem će skloniti sve uslove sa ovog zadatka i moći ćeš odmah da radiš. Ovaj postupak se zapisuje u istoriji zadatka.
          </p>

          <div className="flex gap-2 justify-end pt-1">
            <button
              className="btn-ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="force-cancel-btn"
            >
              Ne, ostavi kako je
            </button>
            <button
              className="btn-primary"
              onClick={confirm}
              disabled={saving}
              data-testid="force-confirm-btn"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Aktiviraj ipak
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
